/**
 * Filing scenarios (spec 2026-09-26, decision D1).
 *
 * `Scenario` is what the filing is prepared as. `EntityKind` is the coarser
 * billing identity ('smllc' | 'mmllc' | 'ccorp') that Stripe metadata and the
 * checkout prices use; a paid order's stored `service_data.scenario` is either
 * that kind (orders created before qualification existed) or the scenario the
 * qualification resolved (`createFederalTaxOrder`).
 */

export const SCENARIOS = [
  "smllc_no_eci",
  "smllc_eci",
  "ccorp",
  "mmllc",
] as const;
export type Scenario = (typeof SCENARIOS)[number];

export const ENTITY_KINDS = ["smllc", "mmllc", "ccorp"] as const;
export type EntityKind = (typeof ENTITY_KINDS)[number];

/** Today's five ECI questions, moved out of the wizard into qualification. */
export const ECI_QUESTION_IDS = [
  "eci.us_employees",
  "eci.us_office",
  "eci.us_inventory",
  "eci.services_in_us",
  "eci.us_real_estate",
] as const;
export type EciQuestionId = (typeof ECI_QUESTION_IDS)[number];

/** Any "yes" among the five ECI answers means the business has ECI. */
export function answersIndicateEci(
  answers: Partial<Record<string, unknown>>,
): boolean {
  return ECI_QUESTION_IDS.some((id) => answers[id] === true);
}

export function isScenario(value: unknown): value is Scenario {
  return (
    typeof value === "string" &&
    (SCENARIOS as readonly string[]).includes(value)
  );
}

export function isEntityKind(value: unknown): value is EntityKind {
  return (
    typeof value === "string" &&
    (ENTITY_KINDS as readonly string[]).includes(value)
  );
}

/**
 * The entity from the company record: `ccorp`, an LLC with more than one
 * member → `mmllc`, anything else → `smllc` (the API's historical rule).
 */
export function entityKindFor(
  companyType: string | null | undefined,
  memberCount: number,
): EntityKind {
  const normalized = String(companyType ?? "")
    .trim()
    .toLowerCase();
  if (normalized === "ccorp") return "ccorp";
  if (normalized === "llc" && memberCount > 1) return "mmllc";
  return "smllc";
}

/** Which plan price ids a company buys Standard and Full Compliance on. */
export type PlanPriceTier = "llc" | "ccorp";

/**
 * The plan price follows the filing entity (owner decision 2026-10-22): only a
 * single-member LLC gets the LLC plan price; a multi-member LLC (by member
 * count, whatever the member type) pays the C-Corp plan price, and so does an
 * LLC with a company member.
 */
export function planPriceTierFor(
  companyType: string | null | undefined,
  memberCount: number,
  hasCompanyMember = false,
): PlanPriceTier {
  if (entityKindFor(companyType, memberCount) !== "smllc") return "ccorp";
  return hasCompanyMember ? "ccorp" : "llc";
}

/**
 * The plan price tier the customer app's services catalog offers, from the facts the API Worker's
 * services summary sends: the company row's `company_type` as stored, its member (shareholder) row
 * count and whether any member is a company. An unknown count reads as one member. The API Worker
 * refuses a package checkout on any other tier with this same function, so the two cannot drift.
 */
export function catalogPlanPriceTier(facts: {
  companyType: string | null | undefined;
  memberCount: number | null | undefined;
  hasCompanyMember: boolean;
}): PlanPriceTier {
  return planPriceTierFor(
    facts.companyType,
    facts.memberCount ?? 1,
    facts.hasCompanyMember,
  );
}

/**
 * An LLC with no member rows: its entity kind (and so the return and the
 * price) is unknown, so it may not start or pay for a filing until a member is
 * recorded (NT-62). A C-corp's owners do not decide its return.
 */
export function memberRecordMissing(
  companyType: string | null | undefined,
  memberCount: number,
): boolean {
  const normalized = String(companyType ?? "")
    .trim()
    .toLowerCase();
  return normalized === "llc" && memberCount <= 0;
}

export function resolveScenario(
  companyType: string | null | undefined,
  memberCount: number,
  answers: Partial<Record<string, unknown>>,
): Scenario {
  const kind = entityKindFor(companyType, memberCount);
  if (kind === "ccorp") return "ccorp";
  if (kind === "mmllc") return "mmllc";
  return answersIndicateEci(answers) ? "smllc_eci" : "smllc_no_eci";
}

export function entityKindOfScenario(scenario: Scenario): EntityKind {
  if (scenario === "ccorp") return "ccorp";
  if (scenario === "mmllc") return "mmllc";
  return "smllc";
}

const LEGACY_SCENARIO_MAP: Record<string, Scenario> = {
  smllc_with_eci: "smllc_eci",
  mmllc_no_eci: "mmllc",
  ccorp_no_dividends: "ccorp",
};

/**
 * Any stored scenario spelling → the current union. The bare billing name
 * `smllc` needs `hasEci` to split; without it the filing reads as no-ECI.
 */
export function normalizeScenario(
  raw: unknown,
  opts: { hasEci?: boolean } = {},
): Scenario | null {
  if (typeof raw !== "string") return null;
  const value = raw.trim();
  if (isScenario(value)) return value;
  if (Object.hasOwn(LEGACY_SCENARIO_MAP, value)) {
    return LEGACY_SCENARIO_MAP[value]!;
  }
  if (value === "smllc") return opts.hasEci ? "smllc_eci" : "smllc_no_eci";
  return null;
}

/**
 * The stored `service_data.scenario` values a checkout of billing kind `kind`
 * may find on its order: the kind itself, or a scenario of that kind.
 */
export function storedScenarioValuesFor(kind: EntityKind): string[] {
  return [
    kind,
    ...SCENARIOS.filter((scenario) => entityKindOfScenario(scenario) === kind),
  ].filter((value, index, all) => all.indexOf(value) === index);
}

export function scenarioMatchesEntityKind(
  stored: unknown,
  kind: EntityKind,
): boolean {
  return (
    typeof stored === "string" && storedScenarioValuesFor(kind).includes(stored)
  );
}
